import { ContentCopy, ExpandLess, ExpandMore, FindInPage } from "@mui/icons-material";
import Logger from "../../../services/Logger";
import { Alert, Box, Button, Card, CardContent, CircularProgress, Collapse, FormControl, IconButton, InputLabel, MenuItem, Select, Snackbar, TextField, Typography } from "@mui/material";
import React, { useState } from "react";
import TopicService from "./TopicService";

export default function RetrieveNews({ onExtract }) {
    const [keyword, setKeyword] = useState("");
    const [newsResults, setNewsResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [expandedArticle, setExpandedArticle] = useState(null);
    const [apiSource, setApiSource] = useState("guardian"); // guardian, newsdata or currents
    const [copySuccess, setCopySuccess] = useState(false);
    const [extractLoading, setExtractLoading] = useState(null); // Track which article is being extracted

    const handleSearch = async () => {
        setLoading(true);
        setError("");
        setExpandedArticle(null); // Reset expanded article when searching

        try {
            let results;
            if (apiSource === "guardian") {
                results = await TopicService.fetchNewsFromGuardian(keyword);
            } else if (apiSource === "currents") {
                results = await TopicService.fetchNewsFromCurrents(keyword);
            } else {
                results = await TopicService.fetchNews(keyword);
            }
            setNewsResults(results);
        } catch (err) {
            setError(err.message);
            setNewsResults([]);
        } finally {
            setLoading(false);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    };

    const handleExpandArticle = (index) => {
        setExpandedArticle(expandedArticle === index ? null : index);
    };

    const handleCopyContent = async (content) => {
        try {
            await navigator.clipboard.writeText(content);
            setCopySuccess(true);
        } catch (err) {
            Logger.error('Failed to copy content: ', err);
            // Fallback for older browsers
            const textArea = document.createElement('textarea');
            textArea.value = content;
            document.body.appendChild(textArea);
            textArea.select();
            try {
                document.execCommand('copy');
                setCopySuccess(true);
            } catch (fallbackErr) {
                Logger.error('Fallback copy failed: ', fallbackErr);
            }
            document.body.removeChild(textArea);
        }
    };

    const handleCloseCopySnackbar = () => {
        setCopySuccess(false);
    };

    const handleExtractClick = async (title, content, articleIndex) => {
        setExtractLoading(articleIndex);
        try {
            const result = await TopicService.extractContent(content);
            Logger.log('Extract result:', result);
            onExtract(title, result);
            // You can add success notification here if needed

        } catch (err) {
            Logger.error('Failed to extract content: ', err);
            setError(`Failed to extract content: ${err.message}`);
        } finally {
            setExtractLoading(null);
        }
    };

    return (
        <Box>
            <Typography variant="h6" gutterBottom>
                Latest News Search
            </Typography>

            <Box sx={{ mb: 2 }}>
                <FormControl fullWidth margin="normal" sx={{ mb: 2 }}>
                    <InputLabel id="api-source-label">News API Source</InputLabel>
                    <Select
                        labelId="api-source-label"
                        value={apiSource}
                        label="News API Source"
                        onChange={(e) => setApiSource(e.target.value)}
                    >
                        <MenuItem value="guardian">Guardian Open Platform (Premium Quality)</MenuItem>
                        <MenuItem value="newsdata">NewsData.io</MenuItem>
                        <MenuItem value="currents">Current API (Better Full Content)</MenuItem>
                    </Select>
                </FormControl>

                <TextField
                    label="Search Keywords"
                    variant="outlined"
                    fullWidth
                    value={keyword}
                    onChange={(e) => setKeyword(e.target.value)}
                    onKeyPress={handleKeyPress}
                    margin="normal"
                    placeholder="Enter keywords to search for news..."
                />
            </Box>

            <Box sx={{ mb: 3 }}>
                <Button
                    variant="contained"
                    onClick={handleSearch}
                    disabled={loading}
                    fullWidth
                >
                    {loading ? <CircularProgress size={20} /> : `Search News (${apiSource === 'guardian' ? 'Guardian' : apiSource === 'currents' ? 'Current API' : 'NewsData.io'})`}
                </Button>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            <Box sx={{ maxHeight: "600px", overflowY: "auto" }}>
                {newsResults.length > 0 && (
                    <Typography variant="h6" gutterBottom>
                        Search Results ({newsResults.length} articles) - Source: {apiSource === 'guardian' ? 'Guardian Open Platform' : apiSource === 'currents' ? 'Current API' : 'NewsData.io'}
                    </Typography>
                )}

                {newsResults.map((article, index) => (
                    <Card key={index} sx={{ mb: 2 }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                <Typography variant="h6" component="h3" gutterBottom sx={{ flex: 1, cursor: 'pointer' }}
                                    onClick={() => handleExpandArticle(index)}>
                                    {article.title}
                                </Typography>
                                <IconButton
                                    onClick={() => handleExpandArticle(index)}
                                    size="small"
                                >
                                    {expandedArticle === index ? <ExpandLess /> : <ExpandMore />}
                                </IconButton>
                            </Box>

                            {article.description && (
                                <Typography variant="body2" color="text.secondary" paragraph>
                                    {article.description}
                                </Typography>
                            )}

                            <Typography variant="caption" color="text.secondary">
                                Source: {article.source_id || "Unknown"} |
                                Published: {article.pubDate ? new Date(article.pubDate).toLocaleDateString() : "Unknown"}
                            </Typography>

                            <Collapse in={expandedArticle === index} timeout="auto" unmountOnExit>
                                <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                        <Typography variant="h6">
                                            Full Article Content
                                        </Typography>
                                        <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1, width: '50px' }}>
                                            {article.content && (
                                                <>
                                                    <IconButton
                                                        onClick={() => handleCopyContent(article.content)}
                                                        size="small"
                                                        title="Copy article content to clipboard"
                                                        sx={{
                                                            color: 'primary.main',
                                                            '&:hover': {
                                                                bgcolor: 'primary.main',
                                                                color: 'white'
                                                            }
                                                        }}
                                                    >
                                                        <ContentCopy fontSize="small" />
                                                    </IconButton>
                                                    <IconButton
                                                        onClick={() => handleExtractClick(article.title, article.content, index)}
                                                        size="small"
                                                        title="Extract content analysis"
                                                        disabled={extractLoading === index}
                                                        sx={{
                                                            color: 'secondary.main',
                                                            '&:hover': {
                                                                bgcolor: 'secondary.main',
                                                                color: 'white'
                                                            }
                                                        }}
                                                    >
                                                        {extractLoading === index ? (
                                                            <CircularProgress size={16} />
                                                        ) : (
                                                            <FindInPage fontSize="small" />
                                                        )}
                                                    </IconButton>
                                                </>
                                            )}
                                        </Box>
                                    </Box>

                                    {article.content ? (
                                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                                            {article.content}
                                        </Typography>
                                    ) : (
                                        <Box>
                                            <Typography variant="body2" color="text.secondary" paragraph>
                                                Full content not available in the API response. Click the link below to read the complete article on the source website.
                                            </Typography>
                                            {article.link && (
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    href={article.link}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    sx={{ mt: 1 }}
                                                >
                                                    Read Full Article on Source Website
                                                </Button>
                                            )}
                                        </Box>
                                    )}

                                    {article.image_url && (
                                        <Box sx={{ mt: 2 }}>
                                            <img
                                                src={article.image_url}
                                                alt={article.title}
                                                style={{
                                                    maxWidth: '100%',
                                                    height: 'auto',
                                                    borderRadius: '4px'
                                                }}
                                                onError={(e) => {
                                                    e.target.style.display = 'none';
                                                }}
                                            />
                                        </Box>
                                    )}

                                    {article.keywords && article.keywords.length > 0 && (
                                        <Box sx={{ mt: 2 }}>
                                            <Typography variant="caption" color="text.secondary">
                                                Keywords: {article.keywords.join(', ')}
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>
                            </Collapse>

                            {article.link && (
                                <Box sx={{ mt: 1 }}>
                                    <Button
                                        size="small"
                                        href={article.link}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        Read on Source Website
                                    </Button>
                                </Box>
                            )}
                        </CardContent>
                    </Card>
                ))}

                {!loading && newsResults.length === 0 && keyword && (
                    <Typography variant="body2" color="text.secondary" align="center">
                        No news articles found for "{keyword}"
                    </Typography>
                )}
            </Box>

            <Snackbar
                open={copySuccess}
                autoHideDuration={3000}
                onClose={handleCloseCopySnackbar}
                message="Article content copied to clipboard!"
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            />
        </Box>
    );
}
